import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Star, ArrowSquareOut, Sparkle, Camera, CheckCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CustomerLanding() {
  const { slug } = useParams();
  const [outlet, setOutlet] = useState(null);
  const [sessionId, setSessionId] = useState(localStorage.getItem(`rs_session_${slug}`) || null);
  const [loading, setLoading] = useState(true);
  const [reviewClicked, setReviewClicked] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState(null); // Supabase Storage URL from backend
  const [screenshotPreview, setScreenshotPreview] = useState(null); // local data URL for preview
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/outlet/${slug}`);
        setOutlet(data);
      } catch {
        toast.error("Page not found");
      } finally { setLoading(false); }
    })();
  }, [slug]);

  useEffect(() => {
    if (outlet && !sessionId) {
      api.post(`/public/outlet/${slug}/scan`).then((r) => {
        const sid = r.data.session_id;
        localStorage.setItem(`rs_session_${slug}`, sid);
        setSessionId(sid);
      }).catch(() => {});
    }
  }, [outlet, sessionId, slug]);

  const goToReview = () => {
    setReviewClicked(true);
    window.open(outlet.google_review_url, "_blank", "noopener");
  };

  const onPickFile = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be smaller than 8MB");
      return;
    }
    // local preview
    const reader = new FileReader();
    reader.onload = (ev) => setScreenshotPreview(ev.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/public/outlet/${slug}/screenshot`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setScreenshotUrl(data.url);
      localStorage.setItem(`rs_screenshot_${slug}`, data.url);
      toast.success("Screenshot uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
      setScreenshotPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const removeScreenshot = () => {
    setScreenshotPreview(null);
    setScreenshotUrl(null);
    localStorage.removeItem(`rs_screenshot_${slug}`);
    if (fileRef.current) fileRef.current.value = "";
  };

  const goSpin = () => {
    if (outlet?.require_screenshot && !screenshotUrl) {
      toast.error("Please upload a screenshot first");
      return;
    }
    navigate(`/r/${slug}/spin`);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--ink-muted)]">Loading…</div>;
  if (!outlet) return <div className="min-h-screen flex items-center justify-center">Not found</div>;

  const requiresShot = !!outlet.require_screenshot;
  const canSpin = !!sessionId && (!requiresShot || !!screenshotUrl);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg)" }}>
      <div className="relative flex-1 flex flex-col items-center justify-start px-5 py-10 sm:py-14">
        <div className="absolute inset-0 dot-bg opacity-40 pointer-events-none" />

        <div className="relative w-full max-w-md">
          <div className="text-center">
            <span className="tag" data-testid="customer-eyebrow">
              <Sparkle size={12} weight="fill" /> Special offer inside
            </span>
            <h1 className="display text-4xl sm:text-5xl font-black tracking-tighter mt-4 leading-[1.05]">
              Loved your visit to <span style={{ color: "var(--primary)" }}>{outlet.name}?</span>
            </h1>
            <p className="text-[var(--ink-muted)] mt-3">Share your feedback & get a chance to win a reward.</p>
          </div>

          {/* Stars */}
          <div className="flex justify-center mt-7 gap-1.5">
            {[0,1,2,3,4].map(i => (
              <Star key={i} size={36} weight="fill" color="#FFCC00" className="animate-float" style={{ animationDelay: `${i*0.1}s` }} />
            ))}
          </div>

          {/* Step 1: Share feedback CTA */}
          <div className="card-pop p-7 mt-8" style={{ background: "var(--secondary)" }}>
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Step 1</div>
            <h3 className="display text-xl font-extrabold mt-1">Share your feedback</h3>
            <p className="text-sm text-[var(--ink-muted)] mt-1">Takes about 30 seconds. Your honest feedback helps us serve you better.</p>
            <button
              data-testid="customer-review-btn"
              onClick={goToReview}
              className="btn-primary w-full justify-center mt-4"
            >
              <ArrowSquareOut size={18} weight="bold" /> Share feedback
            </button>
          </div>

          {/* Step 2: Screenshot upload (only if required) */}
          {requiresShot && (
            <div
              className="card-pop p-7 mt-5"
              style={{ background: reviewClicked ? "var(--pastel-blue)" : "#fff" }}
              data-testid="customer-screenshot-section"
            >
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Step 2</div>
              <h3 className="display text-xl font-extrabold mt-1">Upload screenshot of your feedback</h3>
              <p className="text-sm text-[var(--ink-muted)] mt-1">Snap a screenshot of your submitted feedback and upload it below.</p>

              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={onFileChange}
                className="hidden"
                data-testid="customer-screenshot-input"
              />

              {!screenshotPreview ? (
                <button
                  onClick={onPickFile}
                  disabled={uploading}
                  data-testid="customer-screenshot-pick-btn"
                  className="mt-4 w-full rounded-xl border-[1.5px] border-dashed border-[var(--ink)] py-8 flex flex-col items-center justify-center gap-2 hover:bg-[var(--bg)] transition-colors"
                >
                  <Camera size={32} weight="bold" />
                  <div className="text-sm font-bold">{uploading ? "Uploading…" : "Take photo or choose from gallery"}</div>
                  <div className="text-xs text-[var(--ink-muted)]">PNG, JPG up to 8MB</div>
                </button>
              ) : (
                <div className="mt-4">
                  <div className="relative">
                    <img
                      src={screenshotPreview}
                      alt="Review screenshot preview"
                      data-testid="customer-screenshot-preview"
                      className="w-full max-h-72 object-contain rounded-xl border-[1.5px] border-[var(--ink)] bg-white"
                    />
                    <button
                      onClick={removeScreenshot}
                      data-testid="customer-screenshot-remove-btn"
                      className="absolute top-2 right-2 h-9 w-9 rounded-full bg-white border-[1.5px] border-[var(--ink)] flex items-center justify-center hover:bg-[var(--secondary)]"
                      aria-label="Remove screenshot"
                    >
                      <X size={16} weight="bold" />
                    </button>
                  </div>
                  <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                    {screenshotUrl ? (
                      <div className="flex items-center gap-1.5 text-sm text-[var(--success)] font-semibold">
                        <CheckCircle size={16} weight="fill" /> Uploaded
                      </div>
                    ) : (
                      <div className="text-sm text-[var(--ink-muted)]">{uploading ? "Uploading…" : "Ready"}</div>
                    )}
                    <button
                      onClick={onPickFile}
                      disabled={uploading}
                      data-testid="customer-screenshot-change-btn"
                      className="text-xs font-bold underline"
                    >
                      Change photo
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Final CTA: Spin */}
          <div
            className="card-pop p-7 mt-5"
            style={{ background: canSpin ? "var(--pastel-yellow)" : "#fff" }}
          >
            <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">
              {requiresShot ? "Step 3" : "Step 2"}
            </div>
            <h3 className="display text-xl font-extrabold mt-1">Claim your reward</h3>
            <p className="text-sm text-[var(--ink-muted)] mt-1">
              {canSpin
                ? "Tap below to spin the wheel."
                : requiresShot
                  ? "Upload your feedback screenshot to unlock the wheel."
                  : "After sharing your feedback, tap below to spin the wheel."}
            </p>
            <button
              data-testid="customer-done-btn"
              onClick={goSpin}
              className="btn-primary w-full justify-center mt-4"
              disabled={!canSpin}
            >
              I'm done — Spin the wheel
            </button>
          </div>

          {/* Branding removed */}
        </div>
      </div>
    </div>
  );
}
